// ═══════════════════════════════════════════════════════════════════════════
//  TRADES
//
//  Every trade it takes to build a house from the dirt up, in build order, plus
//  the handyman work that keeps one standing. Each trade carries the specific
//  jobs inside it, because the trade name alone is not enough to route work:
//  "Plumbing" does not tell you whether someone sets a water heater or reroutes
//  a sewer line, and sending the wrong one costs a trip charge and a morning.
//
//  This list IS the routing table. A job is matched to a vendor on what they
//  ticked here, so a missing line is work that never reaches anyone. Add to it
//  rather than replacing it: an existing vendor's saved ticks are stored as the
//  exact strings below, and renaming one silently unticks it on every profile
//  that had it.
//
//  Order matters on screen. It runs the way a house actually goes up — dirt,
//  concrete, framing, roof, dry-in, rough-ins, insulation, drywall, finishes,
//  landscape — so a contractor reads it in the order they think in, rather than
//  alphabetically.
// ═══════════════════════════════════════════════════════════════════════════

export const TAXONOMY = [
  {
    cat: 'Site Work & Excavation',
    subs: [
      'Lot clearing', 'Excavation', 'Grading & compaction', 'Trenching',
      'Utility rough-in', 'Septic systems', 'French drains & drainage',
      'Retaining wall excavation', 'Dirt haul-off',
    ],
  },
  {
    cat: 'Concrete & Foundation',
    subs: [
      'Footings', 'Foundation walls', 'Stem walls', 'Slab on grade',
      'Basement floors', 'Rebar & reinforcement', 'Driveways & aprons',
      'Sidewalks & flatwork', 'Patios', 'Stamped & decorative concrete',
      'Concrete cutting & removal', 'Waterproofing & damp-proofing',
    ],
  },
  {
    cat: 'Framing',
    subs: [
      'Floor systems & joists', 'Wall framing', 'Roof framing', 'Truss setting',
      'Sheathing', 'Beams & headers', 'Stair framing', 'Subfloor',
      'Structural repair', 'Steel & post framing',
    ],
  },
  {
    cat: 'Roofing',
    subs: [
      'Asphalt shingle', 'Metal roofing', 'Tile roofing', 'Flat & TPO roofing',
      'Underlayment & dry-in', 'Tear-off & re-roof', 'Roof repair & leaks',
      'Flashing', 'Gutters & downspouts', 'Skylights', 'Storm damage',
    ],
  },
  {
    cat: 'Windows & Doors',
    subs: [
      'Window installation', 'Window replacement', 'Exterior doors',
      'Interior doors', 'Patio & sliding doors', 'Garage doors & openers',
      'Glass & glazing', 'Screens', 'Weatherstripping & seals',
    ],
  },
  {
    cat: 'Siding & Exterior',
    subs: [
      'Vinyl siding', 'Fiber cement siding', 'Wood siding', 'Stucco',
      'Stone & brick veneer', 'Soffit & fascia', 'Exterior trim',
      'House wrap & air barrier', 'Siding repair',
    ],
  },
  {
    cat: 'Masonry & Stone',
    subs: [
      'Brick laying', 'Block walls', 'Natural stone', 'Fireplaces & chimneys',
      'Chimney repair & tuckpointing', 'Retaining walls', 'Pavers',
      'Stone steps & caps',
    ],
  },
  {
    cat: 'Plumbing',
    subs: [
      'Rough-in plumbing', 'Water heaters', 'Tankless water heaters',
      'Fixtures & faucets', 'Toilets', 'Tubs & showers', 'Drain cleaning',
      'Sewer lines & scoping', 'Repiping', 'Gas lines', 'Water softeners',
      'Backflow testing', 'Leak detection & repair', 'Sump pumps',
    ],
  },
  {
    cat: 'Electrical',
    subs: [
      'Rough-in electrical', 'Panel & service upgrades', 'Sub-panels',
      'Outlets & switches', 'Interior lighting', 'Exterior & landscape lighting',
      'Ceiling fans', 'Recessed lighting', 'EV chargers', 'Generators',
      'Low voltage & data', 'Smoke & CO detectors', 'Troubleshooting & repair',
    ],
  },
  {
    cat: 'HVAC',
    subs: [
      'Furnace install & replacement', 'Air conditioning', 'Heat pumps',
      'Mini splits', 'Ductwork', 'Duct cleaning & sealing',
      'Radiant & in-floor heat', 'Bath & range venting', 'Thermostats',
      'Air quality & filtration', 'Service & repair', 'Swamp coolers',
    ],
  },
  {
    cat: 'Insulation',
    subs: [
      'Batt insulation', 'Blown-in insulation', 'Spray foam', 'Rigid board',
      'Attic insulation', 'Crawlspace & rim joist', 'Air sealing',
      'Sound insulation', 'Insulation removal',
    ],
  },
  {
    cat: 'Drywall',
    subs: [
      'Hanging', 'Taping & mudding', 'Texture & knockdown', 'Ceilings',
      'Patch & repair', 'Water damage repair', 'Level 5 finish', 'Sanding',
    ],
  },
  {
    cat: 'Interior & Exterior Painting',
    subs: [
      'Interior painting', 'Exterior painting', 'Cabinet painting & refinishing',
      'Staining & clear coat', 'Priming & prep', 'Deck & fence staining',
      'Wallpaper removal', 'Spray finishing', 'Multi-unit & turnover painting',
    ],
  },
  {
    cat: 'Cabinetry & Countertops',
    subs: [
      'Cabinet installation', 'Custom cabinets', 'Cabinet refacing',
      'Granite & quartz countertops', 'Laminate countertops', 'Butcher block',
      'Backsplash', 'Closet systems', 'Built-in shelving & benches',
      'Vanities',
    ],
  },
  {
    cat: 'Flooring & Tile',
    subs: [
      'Luxury vinyl plank', 'Hardwood install', 'Hardwood refinishing',
      'Laminate', 'Carpet install & repair', 'Floor tile', 'Shower & wall tile',
      'Backsplash tile', 'Epoxy & garage floors', 'Subfloor prep & leveling',
      'Baseboard after flooring', 'Grout & caulk repair',
    ],
  },
  {
    cat: 'Finish Carpentry & Trim',
    subs: [
      'Baseboard', 'Door casing & jambs', 'Crown molding', 'Wainscot & panelling',
      'Interior door hanging', 'Stairs & railings', 'Built-ins', 'Shelving',
      'Mantels', 'Barn doors', 'Trim repair',
    ],
  },
  {
    cat: 'Remodeling & Construction',
    subs: [
      'Kitchen remodel', 'Bathroom remodel', 'Basement finish',
      'Room additions', 'Garage conversion', 'ADU & casita',
      'Whole-home renovation', 'Accessibility & ADA modifications',
      'Rental turnover & make-ready',
    ],
  },
  {
    cat: 'Landscaping & Hardscape',
    subs: [
      'Grading & soil prep', 'Sod & seed', 'Sprinkler install & repair',
      'Drip irrigation', 'Planting & trees', 'Mulch & rock',
      'Paver patios & walkways', 'Retaining walls', 'Fencing', 'Gates',
      'Decks', 'Pergolas & covers', 'Tree trimming & removal',
      'Yard clean-up & maintenance', 'Snow removal',
    ],
  },
  {
    // Everything a handyman actually gets called for. Deliberately long: the
    // small jobs are most of the volume on a property-management account, and
    // a list that stops at "general repairs" cannot route any of them.
    cat: 'Handyman & General Repairs',
    subs: [
      'Drywall patching', 'Paint touch-up', 'Door adjustment & alignment',
      'Door & cabinet hardware', 'Locks & deadbolts', 'Faucet replacement',
      'Toilet repair & replacement', 'Garbage disposal',
      'Under-sink leaks & supply lines', 'Small drain clearing',
      'Outlet & switch replacement', 'Light fixture replacement',
      'Ceiling fan install', 'Smoke & CO detector replacement',
      'Blinds & curtain rods', 'Shelving & closet organizers', 'Cabinet installation',
      'TV mounting',
      'Picture & mirror hanging', 'Furniture assembly',
      'Appliance install & haul-away', 'Dishwasher install', 'Range hood & microwave',
      'Caulking & grout repair', 'Weatherstripping & door sweeps',
      'Screen repair & replacement', 'Minor tile repair', 'Baseboard & trim repair',
      'Gutter cleaning & repair', 'Pressure washing', 'Fence & gate repair',
      'Deck repair & staining', 'Grab bars & safety rails',
      'Mailboxes, house numbers & exterior fixes', 'Garage door tune-up',
      'Punch list & move-out repairs', 'Seasonal & preventive maintenance',
    ],
  },
  {
    cat: 'Demolition & Junk Removal',
    subs: [
      'Interior demolition', 'Selective demolition', 'Full structure demolition',
      'Concrete removal', 'Junk hauling', 'Dumpster service',
      'Estate & hoarder cleanouts', 'Appliance & mattress disposal',
    ],
  },
  {
    cat: 'Cleaning & Final Prep',
    subs: [
      'Construction clean-up', 'Final & detail clean', 'Window cleaning',
      'Carpet cleaning', 'Duct & vent cleaning', 'Pressure washing',
      'Move-in & move-out clean', 'Punch list walk',
    ],
  },
  {
    cat: 'Specialty',
    subs: [
      'Fire & water damage restoration', 'Mold remediation', 'Asbestos & lead abatement',
      'Radon mitigation', 'Pest control', 'Solar install', 'Security & cameras',
      'Smart home', 'Pools & spas', 'Welding & metal fabrication',
      'Elevators & lifts', 'Foundation repair & piering',
    ],
  },
];

// Years of experience, and crew size. Both 1–100 as a plain list rather than a
// number input: a dropdown cannot be typed wrong, and "0" or "1999" in a
// free-text years box is a support call.
// ── Matching a name that was saved before this list said it that way ───────
//
// A trade is stored as its own text, so "Demolition and Junk Removal" saved a
// year ago does not equal "Demolition & Junk Removal" today. The effect was
// silent and looked like a broken app: the card drew "pick from 0", opening it
// showed an empty checklist, and the tech concluded the picker did not work.
//
// So names are compared loosely — case, punctuation, and "and" against "&" —
// and anything that matches is pulled back onto the current spelling. An
// honest miss returns null and the caller says so rather than showing nothing.
const norm = (v) => String(v || '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const BY_CAT = new Map(TAXONOMY.map(x => [norm(x.cat), x.cat]));
const BY_SUB = new Map(TAXONOMY.flatMap(x => x.subs.map(sk => [norm(sk), sk])));

export const matchTrade = (name) => BY_CAT.get(norm(name)) || null;
export const matchSkill = (name) => BY_SUB.get(norm(name)) || null;

// Pull a whole saved list onto the current spelling, dropping nothing: a name
// that matches nothing is kept exactly as the tech typed it.
export const canonicalTrades = (list) => [...new Set((list || []).map(c => matchTrade(c) || c))];
export const canonicalSkills = (list) => [...new Set((list || []).map(sk => matchSkill(sk) || sk))];

export const YEARS_1_100 = Array.from({ length: 100 }, (_, i) => String(i + 1));

export default TAXONOMY;
